import { SystemClaim } from '@/common/rbac';
import {
	formatDateWithPreferences,
	formatRelativeTime,
} from '@arritech/shared/utils';

type ClockFormat = '12-Hour' | '24-Hour';
type DateFormat = 'MM/DD/YYYY' | 'YYYY/MM/DD' | 'DD/MM/YYYY';
type NameFormat = 'FirstName + LastName' | 'LastName + FirstName';
type NullableString = string | null | undefined;

/**
 * Basic https URL validator shared across the app.
 */
const urlRegex: RegExp = /^((https):\/\/)([A-Za-z0-9]+)\.([A-Za-z]{2,})(.*)/;

// This function will take a string time and returns a formatted time string based on the user's preferred time zone and clock format.
// Ex: 13:57 -> 16:57 (if the user's preferred time zone is UTC+3)
// Ex: 13:57 -> 4:57 PM (if the user's preferred clock format is 12-Hour)
/**
 * Formats a time string (HH:mm) using localStorage user preferences.
 * Falls back to the original input when parsing fails.
 */
export const formatTime = (timeString: NullableString): string => {
	if (!timeString) {
		return '';
	}

	const [hoursStr, minutesStr] = timeString.split(':');
	const hours = parseInt(hoursStr, 10);
	const minutes = parseInt(minutesStr, 10);

	if (isNaN(hours) || isNaN(minutes)) {
		return timeString ?? '';
	}

	const now = new Date();
	const time = new Date(
		Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			hours,
			minutes
		)
	);

	let preferredTimeZone = localStorage.getItem('preferredTimeZone') || 'UTC';
	const preferredClockFormat: ClockFormat =
		(localStorage.getItem('preferredClockFormat') as ClockFormat) || '12-Hour';

	const options: Intl.DateTimeFormatOptions = {
		timeZone: preferredTimeZone,
		hour: '2-digit',
		minute: '2-digit',
		hour12: preferredClockFormat === '12-Hour',
	};

	try {
		const formatter = new Intl.DateTimeFormat('en-US', options);
		const formattedParts = formatter.formatToParts(time);

		const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
			formattedParts.find((part) => part.type === type)?.value || '';

		const formattedHours = getPart('hour');
		const formattedMinutes = getPart('minute');
		const period = options.hour12 ? ` ${getPart('dayPeriod')}` : '';

		return `${formattedHours}:${formattedMinutes}${period}`;
	} catch (error) {
		return timeString ?? '';
	}
};

/**
 * Converts a date string to a "time ago" format (e.g., "5 minutes ago").
 * Returns the original input when it cannot be parsed.
 */

export const timeAgo = (dateString: NullableString): string => {
	return formatRelativeTime(dateString, {
		empty: '',
		fallback: dateString ?? '',
	});
};

/**
 * Formats a date string according to stored time zone/date/clock preferences.
 * Returns the original input when it cannot be parsed.
 */
export const cleanDate = (
	dateString: NullableString,
	{ includeSeconds = true }: { includeSeconds?: boolean } = {}
): string => {
	if (!dateString) {
		return '';
	}

	let preferredTimeZone = localStorage.getItem('preferredTimeZone') || 'UTC';
	const preferredDateFormat: DateFormat =
		(localStorage.getItem('preferredDateFormat') as DateFormat) || 'DD/MM/YYYY';
	const preferredClockFormat: ClockFormat =
		(localStorage.getItem('preferredClockFormat') as ClockFormat) || '12-Hour';

	return formatDateWithPreferences(dateString, {
		timeZone: preferredTimeZone,
		dateFormat: preferredDateFormat,
		clockFormat: preferredClockFormat,
		includeSeconds,
		onResolveBrowserDefaultTimeZone: (resolvedTimeZone: string) => {
			localStorage.setItem('preferredTimeZone', resolvedTimeZone);
		},
	});
};

/**
 * Formats a name based on user preference.
 */
export const formatName = (
	firstName?: NullableString,
	surname?: NullableString
): string => {
	if (!firstName && !surname) {
		return '';
	}
	if (!firstName) {
		return surname ?? '';
	}
	if (!surname) {
		return firstName ?? '';
	}
	const preferredNameFormat: NameFormat =
		(localStorage.getItem('preferredNameFormat') as NameFormat) ||
		'FirstName + LastName';

	switch (preferredNameFormat) {
		case 'LastName + FirstName':
			return `${surname}, ${firstName}`;
		case 'FirstName + LastName':
		default:
			return `${firstName} ${surname}`;
	}
};

/**
 * Converts a boolean-like value to a human readable string.
 */
const cleanBool = (boolData?: boolean | null): 'yes' | 'no' => {
	return boolData ? 'yes' : 'no';
};

/**
 * Calculates age from a date of birth.
 */
const calculateAge = (dob: Date | string | number): number => {
	const birthDate = new Date(dob);
	const otherDate = new Date();

	const years = otherDate.getFullYear() - birthDate.getFullYear();

	if (
		otherDate.getMonth() < birthDate.getMonth() ||
		(otherDate.getMonth() == birthDate.getMonth() &&
			otherDate.getDate() < birthDate.getDate())
	) {
		return years - 1;
	}

	return years;
};

/**
 * Retrieves a query string value by key.
 */
const getValueFromQueryString = (
	queryString: string,
	field: string
): string | null => {
	return new URLSearchParams(queryString).get(field);
};

/**
 * Returns the file extension from a file name.
 */
const getFilenameExt = (fileName?: NullableString): string | undefined => {
	if (!fileName) {
		return undefined;
	}

	const lastDotIndex = fileName.lastIndexOf('.');
	if (lastDotIndex === -1) {
		return undefined;
	}

	return fileName.substring(lastDotIndex + 1);
};

const getDocXiFrame = (): string => {
	return "<iframe width='100%' height='100%' src='https://view.officeapps.live.com/op/embed.aspx?src=http%3A%2F%2Fieee802%2Eorg%3A80%2Fsecmail%2FdocIZSEwEqHFr%2Edoc'></iframe>";
};

// const openDocumentNewWindow = (fileName, base64Data) => {
// 	var ext = getFilenameExt(fileName).toLowerCase();
// 	let newWindow = window.open('');
// 	switch (ext) {
// 		case 'pdf':
// 			newWindow.document.write(
// 				"<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
// 					base64Data +
// 					"'></iframe>"
// 			);
// 			break;
// 		default:
// 			newWindow.document.write(
// 				"<img src='data:application/" +
// 					ext +
// 					';base64, ' +
// 					base64Data +
// 					"'></img>"
// 			);
// 	}
// };

/**
 * Opens a new window to preview a file based on its extension.
 * Handles common image types and PDF.
 */
const openDocumentNewWindow = (fileName: string, base64Data: string): void => {
	const ext = (getFilenameExt(fileName) ?? '').toLowerCase();
	let newWindow = window.open('');

	if (!newWindow) return; // Handle pop-up blocker

	if (!ext) return;

	const mimeTypeMap: Record<string, string> = {
		pdf: 'application/pdf',
		png: 'image/png',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		gif: 'image/gif',
	};

	const mimeType = mimeTypeMap[ext] || 'application/octet-stream';

	const fileBlob = base64ToBlob(base64Data, mimeType);
	const blobUrl = URL.createObjectURL(fileBlob);

	if (ext === 'pdf') {
		newWindow.document.write(
			`<iframe width='100%' height='100%' src='${blobUrl}'></iframe>`
		);
	} else {
		newWindow.document.write(`<img  src='${blobUrl}' />`);
	}

	newWindow.document.close();
};

/**
 * Converts a base64 string to a Blob with a given MIME type.
 */
const base64ToBlob = (base64: string, mimeType: string): Blob => {
	const byteCharacters = atob(base64);
	const byteNumbers = new Array<number>(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: mimeType });
};

/**
 * Checks whether a date string is in the past (midnight comparison).
 */
const isExpired = (dateString?: NullableString): boolean => {
	if (dateString === undefined || dateString === null || dateString === '') {
		return false;
	}

	const date = new Date(dateString.split('.')[0]);
	const now = new Date();
	now.setHours(0, 0, 0, 0);

	return date < now;
};

/**
 * Determines if the current claims set grants root privileges.
 */
const isRoot = (claims?: readonly string[]): boolean => {
	return claims?.includes(SystemClaim.Root) ?? false;
};

/**
 * Copies a string or object to the clipboard using a temporary textarea.
 */
const copyStringToClipboard = (str: string | Record<string, unknown>): void => {
	let valueToCopy = str;

	if (typeof str === 'object') {
		try {
			valueToCopy = JSON.stringify(str).replace(/'/g, '"');
		} catch (e) {
			console.log(e);
		}
	}

	// Create new element
	const el = document.createElement('textarea');
	// Set value (string to be copied)
	el.value = String(valueToCopy);
	// Set non-editable to avoid focus and move outside of view
	el.setAttribute('readonly', '');
	el.style.position = 'absolute';
	el.style.left = '-9999px';
	document.body.appendChild(el);
	// Select text inside element
	el.select();
	// Copy text to clipboard
	document.execCommand('copy');
	// Remove temporary element
	document.body.removeChild(el);
};

/**
 * Validates whether a string is a https URL.
 */
const isValidUrl = (url: string): boolean => {
	return urlRegex.test(url);
};

export default {
	isRoot,
	cleanDate,
	cleanBool,
	calculateAge,
	getValueFromQueryString,
	getFilenameExt,
	openDocumentNewWindow,
	isExpired,
	getDocXiFrame,
	copyStringToClipboard,
	isValidUrl,
	urlRegex,
	formatName,
	formatTime,
	timeAgo,
};
